import { formatString, isDefined, isNotDefined, objectValues } from '@hsp/utilities';

import { Uri } from 'constants/uri';
import {
  AudienceParameters,
  AudienceStatus,
  IAudience,
  IAudienceMailCode,
  IAudienceName,
  IAudienceSaveResult,
  IAudienceShop,
  ICloneAudience,
  ICloneMasterFile,
  IClonePrebuilt,
  IMutateAudienceStatus,
  ISortAudienceMailCode,
} from 'types/api/audience';
import { IResult, IServerSidePagingResult } from 'types/api/common';
import { IPasswordChange, IPasswordRule } from 'types/api/user';
import { IUser } from 'types/user';

import { BaseHttpService } from './http/base-http.service';

class UserService extends BaseHttpService {
  constructor() {
    super({ endpoints: objectValues(Uri.User) as string[] });
  }

  activateAudience(
    { id, status, userId }: { id: string; status: AudienceStatus; userId: string },
    cancel = false
  ): Promise<IResult<boolean>> {
    const url = `${Uri.User.Audience}/${id}/activate`;

    return this.put({ id, status, userId }, this.defaultRequestConfig(url, cancel));
  }

  changePassword(change: IPasswordChange): Promise<IResult<IUser>> | undefined {
    return this.put(change, {
      url: Uri.User.PasswordChange,
      withCredentials: true,
    });
  }

  cloneAudience(audienceUid: string, data: ICloneAudience, cancel = false): Promise<IResult<IAudience>> {
    const url = `${Uri.User.Audience}/${audienceUid}/Clone`;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  cloneAudienceShop(
    { audienceUid, shopId }: { audienceUid: string; shopId: string },
    cancel = false
  ): Promise<IResult<IAudienceShop>> {
    const url = `${Uri.User.Audience}/${audienceUid}/Shop/${shopId}/Clone`;

    return this.post({ audienceUid, shopId }, this.defaultRequestConfig(url, cancel));
  }

  cloneMasterFile(audienceUid: string, data: ICloneMasterFile, cancel = false): Promise<IResult<IAudience>> {
    const url = `${Uri.Audience.MasterFile}/${audienceUid}/Clone`;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  clonePrebuilt(audienceUid: string, data: IClonePrebuilt, cancel = false): Promise<IResult<IAudience>> {
    const url = `${Uri.Audience.PreBuilt}/${audienceUid}/Clone`;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  deleteAudience(id: string, cancel = false): Promise<IResult<boolean>> {
    const url = `${Uri.User.Audience}/${id}`;

    return this.delete(this.defaultRequestConfig(url, cancel));
  }

  deleteAudienceShop(id: string, shopId: string, cancel = false): Promise<IResult<boolean>> {
    const url = `${Uri.User.Audience}/${id}/${shopId}`;

    return this.delete(this.defaultRequestConfig(url, cancel));
  }

  getAudience(id: string, cancel = false): Promise<IResult<IAudience>> {
    const url = `${Uri.User.Audience}/${id}`;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getAudienceNames(cancel = false): Promise<IResult<IAudienceName[]>> {
    const url = Uri.User.Names;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getAudiences(parameters?: AudienceParameters, cancel = false): Promise<IServerSidePagingResult<IAudience[]>> {
    const url = Uri.User.Audience;

    if (isDefined(parameters)) {
      const { category, filter, orderType, page, pageSize, showAll, sort, status, userId } = parameters;

      return this.get({
        ...this.defaultRequestConfig(url, cancel),
        params: { category, filter, orderType, page: page, pageSize, showAll, sort, status, userId },
      });
    } else {
      return this.get({
        ...this.defaultRequestConfig(url, cancel),
        params: { page: -1 },
      });
    }
  }

  getAudienceShops({ id, userId }: { id: string; userId?: string }, cancel = false): Promise<IResult<IAudienceShop[]>> {
    const url = formatString(Uri.User.Shops, id);

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { page: -1, userId },
    });
  }

  getMailCode(
    orderNumber: string | null,
    audienceUid: string | null,
    shopUid: string | null | undefined,
    cancel = false
  ): Promise<IResult<IAudienceMailCode>> {
    const url = Uri.User.GetMailCode;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { audienceUid, orderNumber, shopUid },
    });
  }

  getShop(shopId: string, cancel = false): Promise<IResult<IAudienceShop>> {
    const url = formatString(Uri.User.Shop, shopId);

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  passwordRules(): Promise<IResult<IPasswordRule[]>> | undefined {
    return this.get({
      url: Uri.User.PasswordRules,
    });
  }

  saveAudience(data: IAudience, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = Uri.User.Audience;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  saveAudienceShop(data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = Uri.User.AudienceShop;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  saveAudienceShopMultiRfm(data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = Uri.User.AudienceShopMultiRfm;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  sortAudienceMailCodes(data: ISortAudienceMailCode, cancel = false): Promise<IResult<boolean>> {
    const url = Uri.SortAudienceMailCodes;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  updateAudience(
    { data, id, userId }: { data: IAudience; id: string; userId?: string },
    cancel = false
  ): Promise<IResult<IAudience>> {
    const url = `${Uri.User.Audience}/${id}`;

    return this.put({ ...data, userId }, this.defaultRequestConfig(url, cancel));
  }

  updateAudienceShop(
    { data, userId }: { data: IAudienceShop; userId?: string },
    cancel = false
  ): Promise<IResult<IAudienceSaveResult>> {
    if (isNotDefined(data.id)) {
      return Promise.reject();
    }

    const url = formatString(Uri.User.UpdateAudienceShop, data.id);

    return this.put({ ...data, userId }, this.defaultRequestConfig(url, cancel));
  }

  updateAudienceShopMultiRfm(
    { data, userId }: { data: IAudienceShop; userId?: string },
    cancel = false
  ): Promise<IResult<IAudienceSaveResult>> {
    if (isNotDefined(data.id)) {
      return Promise.reject();
    }

    const url = formatString(Uri.User.UpdateAudienceShopMultiRfm, data.id);

    return this.put({ ...data, userId }, this.defaultRequestConfig(url, cancel));
  }

  updateAudienceStatus(
    { audienceUid, ...parameters }: IMutateAudienceStatus,
    cancel = false
  ): Promise<IResult<boolean>> {
    const url = formatString(Uri.User.AudienceStatus, audienceUid);

    return this.put(parameters, this.defaultRequestConfig(url, cancel));
  }
}

export const userService = new UserService();
